import { type ReactElement } from "react";

import { Label } from "@/components/ui/Input";

export type LabelPreviewRotationDeg = 0 | 90 | 180 | 270;

const ROTATION_OPTIONS: ReadonlyArray<{ deg: LabelPreviewRotationDeg; label: string }> = [
  { deg: 0, label: "0° (as printed)" },
  { deg: 90, label: "90° clockwise" },
  { deg: 180, label: "180°" },
  { deg: 270, label: "270° clockwise" },
];

function rulerMarks(inches: number): number[] {
  const step = inches <= 5 ? 0.5 : 1;
  const marks: number[] = [];
  for (let value = 0; value <= inches + 1e-6; value += step) {
    marks.push(Math.round(value * 100) / 100);
  }
  const last = marks[marks.length - 1];
  if (last == null || Math.abs(last - inches) > 0.05) {
    marks.push(Math.round(inches * 100) / 100);
  }
  return marks;
}

function formatInch(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function parseRotationDeg(value: string): LabelPreviewRotationDeg {
  if (value === "90" || value === "180" || value === "270") return Number(value) as LabelPreviewRotationDeg;
  return 0;
}

export function LabelPreviewRotationSelect(props: {
  id: string;
  value: LabelPreviewRotationDeg;
  onChange: (value: LabelPreviewRotationDeg) => void;
}): ReactElement {
  return (
    <div className="flex items-center gap-2">
      <Label htmlFor={props.id} className="whitespace-nowrap text-xs text-slate-500">
        View rotation
      </Label>
      <select
        id={props.id}
        value={props.value}
        onChange={(event) => props.onChange(parseRotationDeg(event.target.value))}
        title="Rotates this preview only. Does not change ZPL or the Git template."
        className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-800 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      >
        {ROTATION_OPTIONS.map((option) => (
          <option key={option.deg} value={option.deg}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function LabelPreviewWithRulers(props: {
  src: string;
  alt: string;
  widthInches: number;
  heightInches: number;
  rotationDeg?: LabelPreviewRotationDeg;
  maxHeightClassName?: string;
}): ReactElement {
  const rotationDeg = props.rotationDeg ?? 0;
  const swap = rotationDeg === 90 || rotationDeg === 270;
  const displayWidth = swap ? props.heightInches : props.widthInches;
  const displayHeight = swap ? props.widthInches : props.heightInches;
  const xMarks = rulerMarks(displayWidth);
  const yMarks = rulerMarks(displayHeight);
  const maxHeightRem = props.maxHeightClassName === "max-h-[36rem]" ? 36 : 32;

  return (
    <div
      className="inline-grid max-h-full max-w-full grid-cols-[1.5rem_minmax(0,auto)] grid-rows-[1.25rem_minmax(0,auto)]"
      style={{
        width: `min(100%, calc(${String(maxHeightRem)}rem * ${String(displayWidth)} / ${String(displayHeight)} + 1.5rem))`,
      }}
    >
      <div className="flex items-end justify-end pr-0.5 pb-0.5 text-[9px] font-medium uppercase leading-none text-slate-400">
        in
      </div>
      <div className="relative h-5 border-b border-slate-300">
        {xMarks.map((mark) => (
          <div
            key={`x-${String(mark)}`}
            className="absolute top-0 flex h-full flex-col items-center"
            style={{ left: `${(mark / displayWidth) * 100}%`, transform: "translateX(-50%)" }}
          >
            <span className={mark % 1 === 0 ? "text-[10px] font-medium text-slate-600" : "text-[9px] text-slate-400"}>
              {formatInch(mark)}
            </span>
            <span className={mark % 1 === 0 ? "mt-auto h-2 w-px bg-slate-500" : "mt-auto h-1 w-px bg-slate-300"} />
          </div>
        ))}
      </div>
      <div className="relative w-6 border-r border-slate-300">
        {yMarks.map((mark) => (
          <div
            key={`y-${String(mark)}`}
            className="absolute right-0 flex w-full items-center justify-end gap-0.5"
            style={{ top: `${(mark / displayHeight) * 100}%`, transform: "translateY(-50%)" }}
          >
            <span className={mark % 1 === 0 ? "text-[10px] font-medium text-slate-600" : "text-[9px] text-slate-400"}>
              {formatInch(mark)}
            </span>
            <span className={mark % 1 === 0 ? "h-px w-2 bg-slate-500" : "h-px w-1 bg-slate-300"} />
          </div>
        ))}
      </div>
      <div
        className="relative w-full overflow-hidden border border-dashed border-slate-400"
      >
        <div
          aria-hidden
          className="block w-full"
          style={{ aspectRatio: `${String(displayWidth)} / ${String(displayHeight)}` }}
        />
        <img
          src={props.src}
          alt={props.alt}
          className="absolute left-1/2 top-1/2 object-contain"
          style={{
            width: swap ? `${(props.widthInches / displayWidth) * 100}%` : "100%",
            height: swap ? `${(props.heightInches / displayHeight) * 100}%` : "100%",
            transform: `translate(-50%, -50%) rotate(${String(rotationDeg)}deg)`,
          }}
        />
      </div>
    </div>
  );
}
